import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {catchError, of, Subscription, switchMap, tap} from 'rxjs';
import {ModalComponent} from 'src/app/utils/modal/modal.component';
import {BookReaderService} from '../../services/book-reader.service';
import {BookReaderApiV3Service} from '../../services/v3/book-reader-api-v3.service';
import {StageMetadata} from '../../model/book-model';
import {SpinnerService} from 'src/app/utils/services/spinner.service';
import {ActivatedRoute, Router} from '@angular/router';
import {EncryptionService} from '../../services/encryption.service';

declare var bootstrap: any;

class BreakpointGroup {
  scrollOffset = 0;
  constructor(public items: Array<any>) {
  }
}

@Component({
  selector: 'app-web-browser',
  templateUrl: './web-browser.component.html',
  styleUrls: ['./web-browser.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class WebBrowserComponent implements OnInit, OnDestroy, AfterViewChecked {
  public static JUMP_TO_THE_FIRST = -Number.MAX_SAFE_INTEGER;
  public static JUMP_TO_THE_LAST = Number.MAX_SAFE_INTEGER;

  ebookContent: SafeHtml
  private _ebookPath: string
  currentBreakpoint = 0
  pageNum: number
  pageSelection: number
  firstPage: number = Infinity
  lastPage: number = -Infinity
  private breakpointGroups: Array<BreakpointGroup>;
  isBookSizeSettingsShown: boolean = false
  zoom: number
  private tooltipReady = false;
  private tooltipInstances: any[] = [];
  isFullscreen = false;

  sizeSubscription: Subscription
  private bookUpdateObserver: MutationObserver;

  @ViewChild('jumpToPageModal', { static: true })
  jumpToPageModal: ModalComponent;
  @ViewChild('bookContainer', {static: true})
  bookContainer: ElementRef;
  @ViewChild('contentContainer', {static: true})
  contentContainer: ElementRef;
  @ViewChild('fullscreenContainer', { static: true })
  fullscreenContainer: ElementRef;

  @Input()
  set ebookPath(value: string){
    if(value) {
      this._ebookPath = value
      this.getEbookAnchors().pipe(
          switchMap(_ => this.loadContent())
        ).subscribe()
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!event || !event.code) {
      return;
    }
    switch (event.code) {
      case 'ArrowLeft':
        this.moveToPreviousPage()
        break
      case 'ArrowRight':
        this.moveToNextPage()
        break
      case 'ArrowDown':
        this.scrollDown(event)
        break
      case 'ArrowUp':
        this.scrollUp(event)
        break
    }
  }
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateBreakpoints()
  }
  @HostListener('document:fullscreenchange', ['$event'])
  onFullscreenChange() {
    const doc = document as any;
    this.isFullscreen = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
    this.tooltipInstances.forEach(t => t.dispose());
    this.tooltipInstances = [];
    this.tooltipReady = false;
    setTimeout(() => {
      this.updateBreakpoints();
    });
  }
  stages: StageMetadata[]

  constructor(
    private bookReaderApiV3: BookReaderApiV3Service,
    private sanitizer: DomSanitizer,
    private bookReaderService: BookReaderService,
    private spinner: SpinnerService,
    private route: ActivatedRoute,
    private router: Router,
    private encryptionService: EncryptionService,
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    this.sizeSubscription = this.bookReaderService.getSize().subscribe(
      size => {
        this.zoom = size
        setTimeout(() => {
          this.updateBreakpoints();
        })
      }
    )
    this.route.queryParams.subscribe(params => {
      const pageNum = params['page']
      this.pageNum = pageNum ? +pageNum : 0
      this.loadContent().subscribe()
    })

    this.bookUpdateObserver = new MutationObserver(_ => {
      this.updateBreakpoints();
    });
    this.bookUpdateObserver.observe(this.contentContainer.nativeElement,
      { attributes: false, childList: true, characterData: false });
  }

  ngAfterViewChecked() {
    if (!this.tooltipReady) {
      const tooltips = this.elRef.nativeElement.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltips.forEach((el: HTMLElement) => {
        const title = el?.getAttribute('title') || el?.getAttribute('data-bs-original-title');
        if (title) {
          el.setAttribute('title', title);
          this.tooltipInstances.push(new bootstrap.Tooltip(el, { container: this.fullscreenContainer.nativeElement }));
        }
      });
      if(this.tooltipInstances.length == tooltips.length)
        this.tooltipReady = true;
    }
  }

  ngOnDestroy(): void {
    if(this.sizeSubscription) this.sizeSubscription.unsubscribe()
    if(this.bookUpdateObserver) this.bookUpdateObserver.disconnect();
    this.tooltipInstances.forEach(t => t.dispose());
    this.tooltipInstances = [];
  }

  loadContent(){
    if (!this.stages) return of(null)
    return this.spinner.trace(
      this.encryptionService.prepareEncryptionKeysIfNeeded().pipe(
        switchMap(_ => this.getEbookContent()),
        catchError((err) => {
          return this.encryptionService.refreshEncryptionKeys().pipe(
            switchMap(_ => this.getEbookContent()),
            catchError((retryErr) => {
              return of(null)
            })
          )
        })
      )
    )
  }

  moveToNextPage(){
    if(this.pageNum != this.lastPage) this.pageNum += 1
    this.updatePageNumberInParams(this.pageNum)
  }

  moveToPreviousPage(){
    if(this.pageNum != this.firstPage) this.pageNum -= 1
    this.updatePageNumberInParams(this.pageNum)
  }

  getEbookAnchors(){
    return this.spinner.trace(this.bookReaderApiV3.getEbookAnchors(this._ebookPath).pipe(
      tap(response => {
        this.stages = response

        this.firstPage = Math.min(...this.stages.flatMap(it => it.start))
        this.lastPage = Math.max(...this.stages.flatMap(it => it.end))

        if(this.pageNum === 0) {
          this.pageNum = this.firstPage
          this.updatePageNumberInParams(this.firstPage)
        }
      })
    ))
  }

  getEbookContent(){
    return this.bookReaderApiV3.getContent(
        this.pageNum,
        this.getEbookPathByPage(),
        1,
        this.encryptionService.getDeviceKey(),
        this.encryptionService.getEncryptionKey())
        .pipe(
          switchMap(encryptedResponse => this.encryptionService.decrypt(encryptedResponse)),
          tap(response => this.ebookContent = this.sanitizer.bypassSecurityTrustHtml(response))
      )
  }

  private getEbookPathByPage() {
    let page = this.pageNum
    return this.stages?.find(it => page >= it.start && page <= it.end)?.path
  }

  showBookSizeSettings(){
    this.isBookSizeSettingsShown = !this.isBookSizeSettingsShown
  }

  changeTextSize(change: -1 | 1){
    this.bookReaderService.changeSize(change)
  }

  toggleFullscreen() {
    if (this.isFullscreen) {
      this.exitFullscreen();
      return;
    }
    this.enterFullscreen();
  }

  jumpToPage(){
    this.pageNum = this.pageSelection
    this.updatePageNumberInParams(this.pageNum)
    this.jumpToPageModal.hide()
  }

  updatePageNumberInParams(pageNumber: number){
    this.router.navigate([], {
      queryParams: { page: pageNumber }
    }).then()
  }

  getZoom(){
    return {zoom: `${this.zoom}`};
  }

  getVariant() {
    return this._ebookPath?.split("=")[1]
  }

  private scrollDown(event: Event) {
    this.onMoveBreakpoint(1);
    event.preventDefault();
  }

  private scrollUp(event: Event) {
    this.onMoveBreakpoint(-1);
    event.preventDefault();
  }


  private onMoveBreakpoint(offset: number) {
    if (this.pageNum + offset > this.lastPage && this.currentBreakpoint >= this.breakpointGroups.length - 1) return
    if (!this.moveBreakpoint(offset)) {
      if (offset > 0)
        this.setInitialBreakpointAfterPageLoad(WebBrowserComponent.JUMP_TO_THE_FIRST);
      else if (this.pageNum + offset > 0) {
        this.setInitialBreakpointAfterPageLoad(WebBrowserComponent.JUMP_TO_THE_LAST);
      }
      this.onMovePage(offset);
    }
  }

  private setInitialBreakpointAfterPageLoad(command: number) {
    if (command !== WebBrowserComponent.JUMP_TO_THE_FIRST && command !== WebBrowserComponent.JUMP_TO_THE_LAST) {
      throw new Error("the value isn't a command. Use JUMP_TO_THE_FIRST or JUMP_TO_THE_LAST");
    }
    this.currentBreakpoint = command;
  }

  private moveBreakpoint(offset: number): boolean {
    return this.jumpToBreakpoint(this.currentBreakpoint + offset);
  }

  private onMovePage(offset: number) {
    if ( this.pageNum + offset > 0 && this.pageNum + offset <= this.lastPage) {
      this.updatePageNumberInParams(this.pageNum + offset)
    }
  }

  private updateBreakpoints() {
    if (this.bookContainer.nativeElement.childCount == 0) return;
    this.calculateJumpBreakpoints();
    this.jumpToCurrentBreakpoint();
  }

  private calculateJumpBreakpoints() {
    const displayHeight = this.contentContainer.nativeElement.clientHeight * this.zoom;
    const keyElements = this.filterByChild(this.bookContainer.nativeElement
      .querySelectorAll('page-number, qa, hwinst, hwline, postqains, preqainst, img, lessonnumber, dict-cont, chart, table.headwords-like, subhwline, idiom, er, section-title, li, p'));
    let breakpointGroups: Array<BreakpointGroup> = [];
    let previousFrameBreakpoint = 0;
    let currentFrameBreakpoint = 0;
    let elementInFrame = 0;
    let currentBreakpointGroup: Array<any> = [];
    for (let i = 0 ; i < keyElements.length ; i++) {
      const currentKeyElement = keyElements[i];
      currentFrameBreakpoint = (currentKeyElement.offsetTop + currentKeyElement.offsetHeight) * this.zoom;
      elementInFrame++;
      currentBreakpointGroup.push(currentKeyElement);
      // break if elements is more than page over the previous
      if (currentFrameBreakpoint - previousFrameBreakpoint > displayHeight) {
        if (elementInFrame > 1) {
          // typical - rewind by 1 element
          i--;
          currentBreakpointGroup.splice(currentBreakpointGroup.length - 1, 1);
          currentFrameBreakpoint = (keyElements[i].offsetTop + keyElements[i].offsetHeight) * this.zoom;
        }
        // this is very specific case. If element is larger than screen size we keep it on the
        if (currentBreakpointGroup.length == 1 && currentBreakpointGroup[0].offsetHeight * this.zoom > displayHeight){
          const partials = Math.ceil( (currentBreakpointGroup[0].offsetHeight * this.zoom ) /  ( displayHeight * 0.8 ) ); //keep 20% additional space
          for (let partial = 0; partial < partials; partial++) {
            const partialGroup = new BreakpointGroup(currentBreakpointGroup);
            partialGroup.scrollOffset = partial;
            breakpointGroups.push(partialGroup);
          }
        } else {
          breakpointGroups.push(new BreakpointGroup(currentBreakpointGroup));
        }
        currentBreakpointGroup = [];
        previousFrameBreakpoint = currentFrameBreakpoint;
        elementInFrame = 0;
      }
    }
    if (currentBreakpointGroup.length > 0) {
      breakpointGroups.push(new BreakpointGroup(currentBreakpointGroup));
    }
    this.breakpointGroups = breakpointGroups;
  }

  private jumpToBreakpoint(value: number): boolean {
    if (value < 0 || value >= this.breakpointGroups.length) return false;
    this.currentBreakpoint = value;
    this.jumpToCurrentBreakpoint();
    return true;
  }

  private jumpToCurrentBreakpoint() {
    if (!this.breakpointGroups) return;
    if (this.currentBreakpoint < 0) {
      this.currentBreakpoint = 0;
      return;
    }
    if (this.currentBreakpoint >= this.breakpointGroups.length) {
      this.currentBreakpoint = this.breakpointGroups.length - 1;
      const breakpointGroup = this.breakpointGroups[this.currentBreakpoint];
      if (breakpointGroup == null || breakpointGroup.items.length === 0) return;
      breakpointGroup.items[0].scrollIntoView()
      return;
    }

    const breakpointGroup = this.breakpointGroups[this.currentBreakpoint];
    breakpointGroup.items[0].scrollIntoView({behavior: 'smooth'});
  }

  private filterByChild(keyElements: NodeList): any {
    const result = [];
    for (let i = 0 ; i < keyElements.length ; i++) {
      const element = keyElements[i] as any;
      if (element.querySelectorAll("li, p").length == 0) {
        result.push(element);
      }
    }
    return result;
  }

  private enterFullscreen() {
    const el = this.fullscreenContainer?.nativeElement as any;
    if (!el) return;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    if (request) {
      request.call(el);
    }
  }

  private exitFullscreen() {
    const doc = document as any;
    const exit = doc.exitFullscreen || doc.webkitExitFullscreen;
    if (exit) {
      exit.call(doc);
    }
  }
}
